import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import { Link, useLoaderData, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PaymentGatewayApi from "../../../../../infrastructure/api/paymentGateway";
import { normalizeRequestsPaymentSummary } from "../../../../components/payment-gateway/paymentGatewayUtils";
import RequestPaymentActionCell from "../../../../components/payment-gateway/RequestPaymentActionCell";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";

import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const CustomerRequestsPage = () => {
  const pageInfo = useGetPageInfo();
  const dataList = useLoaderData("root");
  let navigate = useNavigate();

  // Check permissions once at component level
  const hasEditPermission = useCheckPermission("edit");
  const hasCreatePermission = useCheckPermission("create");

  const [paymentSummary, setPaymentSummary] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PaymentGatewayApi.getRequestsPaymentSummary();
        const { data } = normalizeRequestsPaymentSummary(res);
        if (!cancelled) setPaymentSummary(data ?? []);
      } catch {
        if (!cancelled) setPaymentSummary([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dataList]);

  const headCells = [
    {
      id: "updateOn",
      label: "تاریخ",
      type: "persiandatetime",
    },

    {
      id: "fullName",
      label: "متقاضی",
    },

    {
      id: "referenceID",
      label: "شناسه درخواست",
    },

    {
      id: "plantCapacityTitle",
      label: "ظرفیت",
    },

    {
      id: "powerPlantTypeTitle",
      label: "نوع نیروگاه",
    },

    {
      id: "requestConfirmTitle",
      label: "وضعیت درخواست",
    },

    {
      id: "stepTitle",
      label: "مرحله فعلی",
    },
    {
      id: "paymentOperational",
      label: "پرداخت",
      render: (_, row) => (
        <RequestPaymentActionCell row={row} paymentSummary={paymentSummary} />
      ),
    },
  ];

  const onEditClicked = (row) => {
    if (row.requestConfirm === "NotChecked") navigate(`${row.id}`);
    else {
      toast.warn("فقط امکان ویرایش درخواست هایی که چک نشده اند وجود دارد.");
    }
  };

  const onPriceOfferClicked = (row) => {
    navigate(`/SLN/Customer/PriceOfferContract/${row.id}`);
  };

  const onCheckRequestClicked = (row) => {
    if (row.requestConfirm !== "NotChecked") {
      navigate(`Information?id=${row.id}`);
    } else {
      toast.warn("فقط امکان بررسی درخواست هایی که چک شده اند وجود دارد.");
    }
  };

  const onFetchData = () => {
    navigate();
  };

  const getRowStatus = (row) => {
    switch (row.requestConfirm) {
      case "NotChecked":
        return "gray";
      case "Canceled":
        return "danger";
      case "InProcess":
        return "primary";
      case "Completed":
        return "success";
      default:
        return "warning";
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                {hasCreatePermission && (
                  <Link to={"create"}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                    >
                      ثبت درخواست جدید{" "}
                    </Button>
                  </Link>
                )}

                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />

                <IconButton
                  variant="text"
                  color="primary"
                  onClick={onFetchData}
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <Grid container>
              <Grid item xs={12} md={12}>
                <EnhancedTable
                  rowKey={"id"}
                  rows={dataList}
                  headCells={headCells}
                  defaultSort={{ orderBy: "updateOn", order: "desc" }}
                  pageSize={100}
                  actions={[
                    // دکمه بررسی روند
                    {
                      icon: <ContentPasteSearchIcon />,
                      onClick: onCheckRequestClicked,
                      color: "primary",
                      tooltip: "بررسی روند",
                    },
                    // دکمه ویرایش - فقط با دسترسی edit
                    ...(hasEditPermission
                      ? [
                          {
                            icon: (
                              <EditIcon
                                sx={{ width: "1rem", height: "1rem" }}
                              />
                            ),
                            onClick: onEditClicked,
                            color: "primary",
                            tooltip: "ویرایش",
                          },
                        ]
                      : []),
                  ]}
                  additionalActions={(row) => [
                    {
                      icon: <AssignmentTurnedInIcon sx={{ width: "1rem", height: "1rem" }} />,
                      onClick: onPriceOfferClicked,
                      color: "warning",
                      tooltip: "قیمت و قرارداد پروژه",
                    },
                  ]}
                  getRowStatus={getRowStatus}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CustomerRequestsPage;
